import type { InputHTMLAttributes } from 'react';
import { cn } from '@/lib/cn';

// DESIGN-SYSTEM SS5.2 — label always visible above the field, never
// placeholder-as-label; error text below, linked via aria-describedby.
type Props = InputHTMLAttributes<HTMLInputElement> & {
  label: string;
  name: string;
  error?: string;
  helperText?: string;
};

export function TextField({ label, name, error, helperText, className, ...props }: Props) {
  const errorId = error ? `${name}-error` : undefined;
  const helperId = helperText ? `${name}-helper` : undefined;

  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={name} className="text-sm font-medium text-text-primary">
        {label}
      </label>
      <input
        id={name}
        name={name}
        aria-describedby={errorId ?? helperId}
        aria-invalid={Boolean(error)}
        className={cn(
          'h-10 rounded-sm border bg-surface-card px-3 text-base text-text-primary',
          error ? 'border-state-error' : 'border-border',
          className,
        )}
        {...props}
      />
      {helperText && !error && (
        <p id={helperId} className="text-sm text-text-muted">
          {helperText}
        </p>
      )}
      {error && (
        <p id={errorId} className="text-sm text-state-error">
          {error}
        </p>
      )}
    </div>
  );
}
